import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  RotateCcw, Download, AlertTriangle, DollarSign, Package, 
  FileQuestion, CheckCircle, BarChart3, ListChecks, ArrowUpDown,
  ArrowUp, ArrowDown, Search, FileText
} from "lucide-react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Doughnut, Bar, Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// KPI Tile Component
const KPITile = ({ value, label, subtext, variant = "default" }) => {
  const dotColors = {
    green: "bg-emerald-500",
    red: "bg-red-500",
    amber: "bg-amber-500",
    blue: "bg-blue-500",
    purple: "bg-purple-500",
    teal: "bg-teal-500",
    default: "bg-slate-300"
  };

  const valueColors = {
    green: "text-emerald-700",
    red: "text-red-600",
    amber: "text-amber-600",
    blue: "text-blue-600",
    purple: "text-purple-600",
    teal: "text-teal-600",
    default: "text-slate-800"
  };

  return (
    <div
      className="bg-white rounded-2xl p-5 border border-slate-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] transition-shadow duration-200 flex flex-col justify-between"
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start gap-1.5 mb-3 min-h-[28px]">
        <span className={`w-1.5 h-1.5 rounded-full ${dotColors[variant]} mt-[3px] flex-shrink-0`} />
        <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 leading-tight">{label}</span>
      </div>
      <div>
        <div className={`text-2xl font-bold font-mono tracking-tight ${valueColors[variant]}`}>{value}</div>
        {subtext && <div className="text-[11px] text-slate-400 mt-1.5">{subtext}</div>}
      </div>
    </div>
  );
};

// Progress Bar Component
const ProgressBar = ({ label, value, color }) => (
  <div className="mb-3.5">
    <div className="flex justify-between items-center text-xs mb-1.5">
      <span className="text-slate-500 font-medium">{label}</span>
      <span className="font-bold font-mono text-[11px]" style={{ color }}>{value}%</span>
    </div>
    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
      <div 
        className="h-full rounded-full transition-all duration-500" 
        style={{ width: `${value}%`, backgroundColor: color }}
      />
    </div>
  </div>
);

// Risk Row Component
const RiskRow = ({ title, description, amount, variant = "red" }) => {
  const borderColors = {
    red: "border-l-red-500",
    amber: "border-l-amber-500",
    blue: "border-l-blue-500"
  };
  const amountColors = {
    red: "text-red-600",
    amber: "text-amber-600",
    blue: "text-blue-600"
  };

  return (
    <div className={`flex items-start gap-3 p-3.5 bg-slate-50/60 border-l-[3px] ${borderColors[variant]} rounded-r-xl mb-2.5 last:mb-0`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-700 leading-tight">{title}</p>
        <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">{description}</p>
      </div>
      <div className={`font-bold text-sm font-mono ${amountColors[variant]} whitespace-nowrap flex-shrink-0`}>{amount}</div>
    </div>
  );
};

// Exception Badge Component
const ExceptionBadge = ({ type }) => {
  const config = {
    "Amount Mismatch": { class: "bg-red-50 text-red-600 border-red-100", icon: DollarSign },
    "Quantity Review": { class: "bg-blue-50 text-blue-600 border-blue-100", icon: Package },
    "Missing PO": { class: "bg-amber-50 text-amber-600 border-amber-100", icon: FileQuestion }
  };
  
  const { class: badgeClass, icon: Icon } = config[type] || { class: "bg-slate-50 text-slate-600 border-slate-100", icon: AlertTriangle };
  
  return (
    <span className={`${badgeClass} border px-2 py-0.5 rounded-full text-[11px] font-medium flex items-center gap-1 w-fit`}>
      <Icon size={11} />
      {type}
    </span>
  );
};

// Severity Badge
const SeverityBadge = ({ level }) => {
  const colors = {
    CRITICAL: "bg-red-50 text-red-600",
    HIGH: "bg-amber-50 text-amber-600",
    MEDIUM: "bg-blue-50 text-blue-600",
    LOW: "bg-slate-50 text-slate-500"
  };
  return (
    <span className={`${colors[level] || colors.LOW} px-2.5 py-0.5 rounded-full text-[11px] font-semibold tracking-wide`}>
      {level}
    </span>
  );
};

// Section Card Title - consistent styling
const SectionTitle = ({ dotColor, children }) => (
  <CardTitle className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 flex items-center gap-2">
    <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
    {children}
  </CardTitle>
);

// Table header cell - consistent styling
const TH = ({ children, className = "" }) => (
  <TableHead className={`bg-slate-50/60 text-[11px] uppercase font-semibold tracking-wider text-slate-400 ${className}`}>
    {children}
  </TableHead>
);

// Sortable table header
const SortableTH = ({ children, sortKey, sortState, onSort, className = "" }) => {
  const isActive = sortState.key === sortKey;
  const icon = !isActive ? <ArrowUpDown size={10} className="text-slate-300" /> : sortState.dir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />;
  return (
    <TableHead
      className={`bg-slate-50/60 text-[11px] uppercase font-semibold tracking-wider text-slate-400 cursor-pointer select-none hover:text-slate-600 transition-colors ${className}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">{children} {icon}</span>
    </TableHead>
  );
};

// Filter bar
const FilterBar = ({ search, onSearchChange, typeFilter, onTypeChange, types }) => (
  <div className="flex items-center gap-3 mb-4" data-testid="filter-bar">
    <div className="relative flex-1 max-w-xs">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        type="text"
        value={search}
        onChange={e => onSearchChange(e.target.value)}
        placeholder="Search PO number..."
        className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:border-[#062B55] focus:ring-1 focus:ring-[#062B55]/20 transition-colors"
        data-testid="filter-search"
      />
    </div>
    <select
      value={typeFilter}
      onChange={e => onTypeChange(e.target.value)}
      className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:border-[#062B55] text-slate-600"
      data-testid="filter-type"
    >
      <option value="">All Types</option>
      {types.map(t => <option key={t} value={t}>{t}</option>)}
    </select>
  </div>
);

export default function DashboardScreen({ 
  runId, 
  runData,
  exceptions, 
  onExport,
  onFetchExceptions,
  onNewRun,
  onBack,
  loading 
}) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  // Sort state per table
  const [matchSort, setMatchSort] = useState({ key: null, dir: "asc" });
  const [varianceSort, setVarianceSort] = useState({ key: "excess", dir: "desc" });
  const [seqSort, setSeqSort] = useState({ key: null, dir: "asc" });

  // Filter state
  const [searchFilter, setSearchFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const toggleSort = useCallback((setter) => (key) => {
    setter(prev => ({
      key,
      dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc"
    }));
  }, []);

  const sortData = useCallback((data, sortState, getVal) => {
    if (!sortState.key || !data.length) return data;
    return [...data].sort((a, b) => {
      const va = getVal(a, sortState.key);
      const vb = getVal(b, sortState.key);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp = typeof va === "string" ? va.localeCompare(vb) : va - vb;
      return sortState.dir === "asc" ? cmp : -cmp;
    });
  }, []);

  useEffect(() => {
    if (!runId) {
      navigate("/");
      return;
    }
    if (!exceptions) {
      onFetchExceptions();
    }
  }, [runId, exceptions]);

  // Compute comprehensive metrics from exception data and backend stats
  const metrics = useMemo(() => {
    if (!exceptions?.exceptions) return null;
    
    const exceptionList = exceptions.exceptions;
    const total = exceptionList.length;
    const backendStats = exceptions.stats || {};
    
    // Count by exception type
    const amountMismatches = exceptionList.filter(e => e.exception_type === "Amount Mismatch");
    const quantityMismatches = exceptionList.filter(e => e.exception_type === "Quantity Mismatch");
    const missingPOs = exceptionList.filter(e => e.exception_type === "Missing PO");
    
    // Use backend-computed stats if available, otherwise compute from exceptions
    const stats = {
      // Core counts from backend
      totalPOs: backendStats.total_pos || 0,
      totalGRNs: backendStats.total_grns || 0,
      totalInvoices: backendStats.total_invoices || 0,
      
      // Match status from backend
      fullMatch: backendStats.full_3way_match || 0,
      invoiceOnlyNoGRN: backendStats.invoice_only_no_grn || 0,
      grnOnlyNoInvoice: backendStats.grn_only_no_invoice || 0,
      noGRNNoInvoice: backendStats.no_grn_no_invoice || 0,
      
      // Exception counts
      total,
      amountMismatches: amountMismatches.length,
      quantityMismatches: quantityMismatches.length,
      missingPOs: missingPOs.length,
      missingPOAmount: backendStats.missing_po_amount || missingPOs.reduce((sum, e) => sum + (e.invoice_amount || 0), 0),
      
      // Amount analysis from backend (with tolerance applied)
      overInvoiced: backendStats.over_invoiced_count || 0,
      overInvoicedAmount: backendStats.over_invoiced_amount || 0,
      underInvoiced: backendStats.under_invoiced_count || 0,
      underInvoicedAmount: backendStats.under_invoiced_amount || 0,
      withinTolerance: backendStats.within_tolerance_count || 0,
      totalVariance: backendStats.total_variance || 0,
      
      // Sequencing violations from backend
      seq_inv_before_po_count: backendStats.seq_inv_before_po_count || 0,
      seq_inv_before_grn_count: backendStats.seq_inv_before_grn_count || 0,
      seq_no_po_count: backendStats.seq_no_po_count || 0,
      seq_no_po_pct: backendStats.seq_no_po_pct || 0,
      seq_paid_before_grn_count: backendStats.seq_paid_before_grn_count || 0,
      seq_inv_before_po_details: backendStats.seq_inv_before_po_details || [],
      seq_inv_before_grn_details: backendStats.seq_inv_before_grn_details || [],
      seq_monthly_rates: backendStats.seq_monthly_rates || [],
    };
    
    // Calculate match rate
    const matchRate = stats.totalPOs > 0 
      ? ((stats.fullMatch / stats.totalPOs) * 100).toFixed(1)
      : "0.0";
    
    // Variance bands - categorize amount mismatches by variance percentage
    const varianceBands = {
      "< -20%": 0,
      "-20% to -10%": 0,
      "-10% to 0%": 0,
      "0% to 10%": 0,
      "10% to 20%": 0,
      "> 20%": 0
    };
    
    amountMismatches.forEach(e => {
      const pct = e.variance_pct || 0;
      if (pct < -20) varianceBands["< -20%"]++;
      else if (pct < -10) varianceBands["-20% to -10%"]++;
      else if (pct < 0) varianceBands["-10% to 0%"]++;
      else if (pct < 10) varianceBands["0% to 10%"]++;
      else if (pct < 20) varianceBands["10% to 20%"]++;
      else varianceBands["> 20%"]++;
    });

    return {
      ...stats,
      matchRate,
      varianceBands,
      exceptionsByType: {
        "Amount Mismatch": amountMismatches,
        "Quantity Mismatch": quantityMismatches,
        "Missing PO": missingPOs
      }
    };
  }, [exceptions]);

  // Filtered + sorted exception list for Match Status tab
  const filteredExceptions = useMemo(() => {
    if (!exceptions?.exceptions) return [];
    let list = exceptions.exceptions;
    if (searchFilter) list = list.filter(e => (e.po_number || "").toLowerCase().includes(searchFilter.toLowerCase()));
    if (typeFilter) list = list.filter(e => e.exception_type === typeFilter);
    return sortData(list, matchSort, (item, key) => {
      if (key === "po_number") return item.po_number || "";
      if (key === "type") return item.exception_type || "";
      if (key === "vendor") return item.vendor_id || "";
      return item[key];
    });
  }, [exceptions, searchFilter, typeFilter, matchSort, sortData]);

  const exceptionTypes = useMemo(() => {
    if (!exceptions?.exceptions) return [];
    return [...new Set(exceptions.exceptions.map(e => e.exception_type))].filter(Boolean);
  }, [exceptions]);

  // Chart configurations
  const chartColors = {
    green: '#059669',
    red: '#dc2626',
    amber: '#d97706',
    blue: '#2563eb',
    purple: '#7c3aed',
    teal: '#0d9488',
    gray: '#9ca3af'
  };

  // Donut chart for match status
  const matchStatusDonut = useMemo(() => {
    if (!metrics) return null;
    return {
      labels: ['Full 3-Way Match', 'Invoice Only (No GRN)', 'GRN Only (No Invoice)', 'No GRN & No Invoice'],
      datasets: [{
        data: [metrics.fullMatch, metrics.invoiceOnlyNoGRN, metrics.grnOnlyNoInvoice, metrics.noGRNNoInvoice],
        backgroundColor: [chartColors.green, chartColors.red, chartColors.amber, chartColors.gray],
        borderColor: '#ffffff',
        borderWidth: 2
      }]
    };
  }, [metrics]);

  // Exception type distribution donut
  const exceptionTypeDonut = useMemo(() => {
    if (!metrics) return null;
    return {
      labels: ['Amount Mismatch (>±10%)', 'Quantity Mismatch (>±2%)', 'Missing PO'],
      datasets: [{
        data: [metrics.amountMismatches, metrics.quantityMismatches, metrics.missingPOs],
        backgroundColor: [chartColors.red, chartColors.amber, chartColors.gray],
        borderColor: '#ffffff',
        borderWidth: 2
      }]
    };
  }, [metrics]);

  // Exception count bar chart
  const exceptionCountBar = useMemo(() => {
    if (!metrics) return null;
    return {
      labels: ['Amount Mismatch', 'Quantity Mismatch', 'Missing PO'],
      datasets: [{
        data: [metrics.amountMismatches, metrics.quantityMismatches, metrics.missingPOs],
        backgroundColor: [chartColors.red, chartColors.amber, chartColors.gray],
        borderRadius: 6
      }]
    };
  }, [metrics]);

  // Variance band bar chart
  const varianceBandData = useMemo(() => {
    if (!metrics) return null;
    const labels = Object.keys(metrics.varianceBands);
    const data = Object.values(metrics.varianceBands);
    const colors = labels.map(k => {
      // Under-invoiced (negative) - blue
      if (k.includes('-') && !k.startsWith('>')) return chartColors.blue;
      // Severely over-invoiced - red
      if (k === '> 20%' || k === '10% to 20%') return chartColors.red;
      // Moderately over-invoiced - amber
      if (k === '5% to 10%') return chartColors.amber;
      // Within tolerance - green
      return chartColors.green;
    });
    return {
      labels,
      datasets: [{
        data,
        backgroundColor: colors,
        borderRadius: 6
      }]
    };
  }, [metrics]);

  // Billing status donut
  const billingStatusDonut = useMemo(() => {
    if (!metrics) return null;
    const withinTolerance = Math.max(0, (metrics.totalPOsReviewed || metrics.totalPOs || 0) - metrics.overInvoiced - metrics.underInvoiced);
    return {
      labels: ['Over-invoiced (>10%)', 'Under-invoiced (>10%)', 'Within Tolerance'],
      datasets: [{
        data: [metrics.overInvoiced, metrics.underInvoiced, withinTolerance],
        backgroundColor: [chartColors.red, chartColors.blue, chartColors.green],
        borderColor: '#ffffff',
        borderWidth: 2
      }]
    };
  }, [metrics]);

  // Exception type summary bar (for sequencing tab)
  const seqViolationsBar = useMemo(() => {
    if (!metrics) return null;
    return {
      labels: ['Amount Mismatch', 'Quantity Issues', 'Missing PO'],
      datasets: [{
        data: [metrics.amountMismatches, metrics.quantityMismatches, metrics.missingPOs],
        backgroundColor: [chartColors.red, chartColors.amber, chartColors.gray],
        borderRadius: 6
      }]
    };
  }, [metrics]);

  // Payment risk bars - Amount
  const paymentRiskAmountData = useMemo(() => {
    if (!metrics) return null;
    return {
      labels: ['Over-Invoiced', 'Missing PO', 'Under-Invoiced'],
      datasets: [{
        label: '$ at Risk',
        data: [
          metrics.overInvoicedAmount,
          metrics.missingPOAmount,
          Math.abs(metrics.totalVariance - metrics.overInvoicedAmount) // Under-invoiced amount
        ],
        backgroundColor: [chartColors.red, chartColors.amber, chartColors.blue],
        borderRadius: 6
      }]
    };
  }, [metrics]);

  // Payment risk bars - Count
  const paymentRiskCountData = useMemo(() => {
    if (!metrics) return null;
    return {
      labels: ['Amount Mismatch', 'Missing PO', 'Quantity Issues'],
      datasets: [{
        label: '# Exceptions',
        data: [metrics.amountMismatches, metrics.missingPOs, metrics.quantityMismatches],
        backgroundColor: [chartColors.red, chartColors.amber, chartColors.blue],
        borderRadius: 6
      }]
    };
  }, [metrics]);

  // Chart options
  const fontFamily = "'Inter', -apple-system, sans-serif";

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: {
        position: 'right',
        labels: {
          font: { size: 11, family: fontFamily },
          padding: 12,
          usePointStyle: true,
          pointStyleWidth: 8,
          color: '#64748b'
        }
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
            const pct = total > 0 ? ((ctx.raw / total) * 100).toFixed(1) : 0;
            return `${ctx.label}: ${ctx.raw} (${pct}%)`;
          }
        }
      }
    }
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
            const pct = total > 0 ? ((ctx.raw / total) * 100).toFixed(1) : 0;
            return `${ctx.raw} (${pct}%)`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { font: { size: 11, family: fontFamily }, color: '#94a3b8' }
      },
      y: {
        grid: { color: '#f1f5f9', drawBorder: false },
        border: { display: false },
        ticks: { font: { size: 11, family: fontFamily }, color: '#94a3b8' },
        beginAtZero: true
      }
    }
  };

  const horizontalBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: { legend: { display: false } },
    scales: {
      x: {
        grid: { color: '#f1f5f9', drawBorder: false },
        border: { display: false },
        ticks: { font: { size: 11, family: fontFamily }, color: '#94a3b8' },
        beginAtZero: true
      },
      y: {
        grid: { display: false },
        border: { display: false },
        ticks: { font: { size: 11, family: fontFamily }, color: '#94a3b8' }
      }
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await onExport(true);
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API}/api/runs/${runId}/export-pdf`, { method: "POST" });
      if (!response.ok) throw new Error("PDF export failed");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `exception_report_${runId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setExportingPdf(false);
    }
  };

  if (!exceptions || !metrics) {
    return (
      <div className="max-w-6xl mx-auto animate-fadeIn">
        <div className="empty-state py-16">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <BarChart3 size={28} className="text-slate-400" />
          </div>
          <p className="text-lg font-semibold text-slate-700">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const exceptionList = exceptions.exceptions || [];

  return (
    <div className="max-w-7xl mx-auto animate-fadeIn">
      {/* Dashboard Header */}
      <div className="bg-gradient-to-br from-[#062B55] to-[#0a3d7a] rounded-2xl p-6 mb-8 border border-[rgba(111,168,247,0.15)] shadow-[0_4px_24px_-4px_rgba(6,43,85,0.25)]">
        <div className="flex flex-wrap justify-between items-start gap-4">
          <div className="text-white">
            <h1 className="text-xl font-semibold tracking-tight" data-testid="dashboard-headline">
              Three-Way Match - <span className="text-[#9FC4FF]">PO · GRN · Invoice</span>
            </h1>
            <p className="text-slate-300/80 text-sm mt-1.5 font-light">
              AP Governance Analysis · {metrics.totalPOs} Purchase Orders · {metrics.total} Exceptions
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="bg-white/[0.06] text-slate-200 text-[11px] font-medium px-3.5 py-1.5 rounded-full border border-white/[0.1] backdrop-blur-sm">
              3-Way Match Rate: {metrics.matchRate}%
            </span>
            {metrics.amountMismatches > 0 && (
              <span className="bg-red-500/[0.12] text-red-200 text-[11px] font-medium px-3.5 py-1.5 rounded-full border border-red-400/20">
                {metrics.amountMismatches} Amount Mismatches (&gt;±10%)
              </span>
            )}
            {metrics.missingPOs > 0 && (
              <span className="bg-amber-500/[0.12] text-amber-200 text-[11px] font-medium px-3.5 py-1.5 rounded-full border border-amber-400/20">
                {metrics.missingPOs} No-PO Items
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Tabs - All 5 from HTML */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-transparent w-full justify-start rounded-none h-auto p-0 mb-8 overflow-x-auto flex-nowrap border-b border-slate-200/80">
          {[
            { value: "overview", label: "Overview" },
            { value: "match", label: "Match Status" },
            { value: "variance", label: "Amount Variance" },
            { value: "seq", label: "Sequencing Violations" },
            { value: "pay", label: "Materiality Risk" }
          ].map(tab => (
            <TabsTrigger 
              key={tab.value}
              value={tab.value} 
              className="data-[state=active]:border-b-2 data-[state=active]:border-[#062B55] data-[state=active]:text-[#062B55] data-[state=active]:shadow-none rounded-none px-5 py-3 text-[11px] font-semibold uppercase tracking-widest whitespace-nowrap text-slate-400 hover:text-slate-600 transition-colors"
              data-testid={`tab-${tab.value}`}
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* TAB 1: OVERVIEW */}
        <TabsContent value="overview" className="mt-0">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <KPITile value={`${metrics.matchRate}%`} label="Full 3-Way Match Rate" subtext={`${metrics.fullMatch} of ${metrics.totalPOs} POs`} variant="green" />
            <KPITile value={metrics.invoiceOnlyNoGRN} label="Invoice Only (No GRN)" subtext="Highest risk" variant={metrics.invoiceOnlyNoGRN > 0 ? "red" : "green"} />
            <KPITile value={metrics.missingPOs} label="No-PO Invoices" subtext={`${metrics.totalInvoices > 0 ? ((metrics.missingPOs / metrics.totalInvoices) * 100).toFixed(1) : 0}% of invoices`} variant={metrics.missingPOs > 0 ? "amber" : "green"} />
            <KPITile value={metrics.amountMismatches} label="Amount Mismatch (&gt;±10%)" subtext="Outside tolerance" variant={metrics.amountMismatches > 0 ? "red" : "green"} />
            <KPITile value={metrics.quantityMismatches} label="Qty Mismatch (&gt;±2%)" subtext="PO vs GRN qty" variant={metrics.quantityMismatches > 0 ? "amber" : "green"} />
            <KPITile value={metrics.overInvoiced} label="Over-Invoiced (&gt;10%)" subtext="Invoice &gt; PO" variant={metrics.overInvoiced > 0 ? "red" : "green"} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 mb-6">
            {/* Key Risk Areas - 3 columns */}
            <Card className="card-surface lg:col-span-3">
              <CardHeader className="pb-3 pt-5 px-6">
                <SectionTitle dotColor="bg-red-500">Key Risk Areas - Financial Exposure</SectionTitle>
              </CardHeader>
              <CardContent className="px-6 pb-5">
                {metrics.overInvoiced > 0 && (
                  <RiskRow 
                    title="Over-Invoiced POs (&gt;10%)" 
                    description={`${metrics.overInvoiced} POs where invoice exceeds PO by more than 10%`}
                    amount={`₹${metrics.overInvoicedAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}
                    variant="red"
                  />
                )}
                {metrics.missingPOs > 0 && (
                  <RiskRow 
                    title="Missing PO References" 
                    description={`${metrics.missingPOs} invoices reference non-existent purchase orders`}
                    amount={`₹${metrics.missingPOAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}
                    variant="red"
                  />
                )}
                {metrics.invoiceOnlyNoGRN > 0 && (
                  <RiskRow 
                    title="Invoice Without GRN" 
                    description={`${metrics.invoiceOnlyNoGRN} POs have invoice but no goods receipt`}
                    amount={`${metrics.invoiceOnlyNoGRN} POs`}
                    variant="amber"
                  />
                )}
                {metrics.quantityMismatches > 0 && (
                  <RiskRow 
                    title="Quantity Discrepancies (&gt;±2%)" 
                    description={`${metrics.quantityMismatches} POs with GRN qty outside ±2% tolerance`}
                    amount={`${metrics.quantityMismatches} items`}
                    variant="blue"
                  />
                )}
                {metrics.total === 0 && (
                  <div className="text-center py-6 text-emerald-600 font-medium text-sm">
                    No risk areas identified - all records matched successfully
                  </div>
                )}
              </CardContent>
            </Card>

            {/* PO Match Status Distribution - 2 columns */}
            <Card className="card-surface lg:col-span-2">
              <CardHeader className="pb-3 pt-5 px-6">
                <SectionTitle dotColor="bg-blue-500">PO Match Status Distribution</SectionTitle>
              </CardHeader>
              <CardContent className="px-6 pb-5">
                <div className="h-[160px] mb-5">
                  {matchStatusDonut && <Doughnut data={matchStatusDonut} options={donutOptions} />}
                </div>
                <ProgressBar label="Full 3-Way Match" value={parseFloat(metrics.matchRate)} color={chartColors.green} />
                <ProgressBar label="Invoice Only (No GRN)" value={metrics.totalPOs > 0 ? ((metrics.invoiceOnlyNoGRN / metrics.totalPOs) * 100).toFixed(1) : 0} color={chartColors.red} />
                <ProgressBar label="GRN Only (No Invoice)" value={metrics.totalPOs > 0 ? ((metrics.grnOnlyNoInvoice / metrics.totalPOs) * 100).toFixed(1) : 0} color={chartColors.amber} />
                <ProgressBar label="No GRN & No Invoice" value={metrics.totalPOs > 0 ? ((metrics.noGRNNoInvoice / metrics.totalPOs) * 100).toFixed(1) : 0} color={chartColors.gray} />
              </CardContent>
            </Card>
          </div>

          {/* Exception Count Bar Chart */}
          <Card className="card-surface">
            <CardHeader className="pb-3 pt-5 px-6">
              <SectionTitle dotColor="bg-purple-500">Exception Count by Type</SectionTitle>
            </CardHeader>
            <CardContent className="px-6 pb-5">
              <div className="h-[200px]">
                {exceptionCountBar && <Bar data={exceptionCountBar} options={barOptions} />}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 2: MATCH STATUS */}
        <TabsContent value="match" className="mt-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <KPITile value={metrics.fullMatch} label="Full 3-Way Match" subtext={`${metrics.matchRate}% match rate`} variant="green" />
            <KPITile value={metrics.amountMismatches} label="Amount Mismatches" subtext=">±10% variance" variant="red" />
            <KPITile value={metrics.quantityMismatches} label="Quantity Mismatches" subtext=">±2% GRN variance" variant="amber" />
            <KPITile value={metrics.missingPOs} label="Missing PO Refs" subtext="Invalid PO reference" variant="blue" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
            <Card className="card-surface">
              <CardHeader className="pb-3 pt-5 px-6">
                <SectionTitle dotColor="bg-blue-500">Match Status Distribution</SectionTitle>
              </CardHeader>
              <CardContent className="px-6 pb-5">
                <div className="h-[260px]">
                  {matchStatusDonut && <Doughnut data={matchStatusDonut} options={donutOptions} />}
                </div>
              </CardContent>
            </Card>

            <Card className="card-surface">
              <CardHeader className="pb-3 pt-5 px-6">
                <SectionTitle dotColor="bg-red-500">Exception Type Distribution</SectionTitle>
              </CardHeader>
              <CardContent className="px-6 pb-5">
                <div className="h-[260px]">
                  {exceptionTypeDonut && <Doughnut data={exceptionTypeDonut} options={donutOptions} />}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Exception List Table */}
          <Card className="card-surface">
            <CardHeader className="pb-3 pt-5 px-6">
              <SectionTitle dotColor="bg-slate-400">Exception Details</SectionTitle>
            </CardHeader>
            <CardContent className="px-6 pb-5">
              <FilterBar search={searchFilter} onSearchChange={setSearchFilter} typeFilter={typeFilter} onTypeChange={setTypeFilter} types={exceptionTypes} />
              <ScrollArea className="h-[300px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableTH sortKey="po_number" sortState={matchSort} onSort={toggleSort(setMatchSort)}>PO Number</SortableTH>
                      <SortableTH sortKey="type" sortState={matchSort} onSort={toggleSort(setMatchSort)}>Type</SortableTH>
                      <TH>Description</TH>
                      <SortableTH sortKey="vendor" sortState={matchSort} onSort={toggleSort(setMatchSort)}>Vendor</SortableTH>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredExceptions.map((exc, idx) => (
                      <TableRow key={idx} className="hover:bg-slate-50/40">
                        <TableCell className="font-mono text-sm">{exc.po_number}</TableCell>
                        <TableCell><ExceptionBadge type={exc.exception_type} /></TableCell>
                        <TableCell className="text-sm text-slate-500 max-w-[300px] truncate">{exc.description}</TableCell>
                        <TableCell className="text-sm text-slate-500">{exc.vendor_id || '-'}</TableCell>
                      </TableRow>
                    ))}
                    {filteredExceptions.length === 0 && (
                      <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-6 text-sm">No matching exceptions</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 3: AMOUNT VARIANCE */}
        <TabsContent value="variance" className="mt-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <KPITile value={metrics.overInvoiced} label="Over-Invoiced POs" subtext="Invoice >10% above PO" variant="red" />
            <KPITile value={metrics.underInvoiced} label="Under-Invoiced POs" subtext="Invoice >10% below PO" variant="blue" />
            <KPITile value={Math.max(0, (metrics.totalPOsReviewed || metrics.totalPOs || 0) - metrics.overInvoiced - metrics.underInvoiced)} label="Within Tolerance" subtext="Variance within ±10%" variant="green" />
            <KPITile value={metrics.overInvoiced} label="Partial GRN Overbilled" subtext="Invoice > goods received" variant="amber" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
            <Card className="card-surface">
              <CardHeader className="pb-3 pt-5 px-6">
                <SectionTitle dotColor="bg-red-500">PO Billing Status Distribution</SectionTitle>
              </CardHeader>
              <CardContent className="px-6 pb-5">
                <div className="h-[240px]">
                  {billingStatusDonut && <Doughnut data={billingStatusDonut} options={donutOptions} />}
                </div>
              </CardContent>
            </Card>

            <Card className="card-surface">
              <CardHeader className="pb-3 pt-5 px-6">
                <SectionTitle dotColor="bg-purple-500">Invoice vs PO Variance Band</SectionTitle>
              </CardHeader>
              <CardContent className="px-6 pb-5">
                <div className="h-[240px]">
                  {varianceBandData && <Bar data={varianceBandData} options={{...barOptions, scales: {...barOptions.scales, x: {...barOptions.scales.x, ticks: {...barOptions.scales.x.ticks, maxRotation: 45, font: {size: 9, family: fontFamily}}}}}} />}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Over-Invoiced Table */}
          <Card className="card-surface">
            <CardHeader className="pb-3 pt-5 px-6">
              <SectionTitle dotColor="bg-red-500">Top Over-Invoiced POs - Highest Variance</SectionTitle>
            </CardHeader>
            <CardContent className="px-6 pb-5">
              <ScrollArea className="h-[300px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableTH sortKey="po" sortState={varianceSort} onSort={toggleSort(setVarianceSort)}>PO ID</SortableTH>
                      <SortableTH sortKey="vendor" sortState={varianceSort} onSort={toggleSort(setVarianceSort)}>Vendor</SortableTH>
                      <SortableTH sortKey="po_amt" sortState={varianceSort} onSort={toggleSort(setVarianceSort)} className="text-right">PO Amount</SortableTH>
                      <SortableTH sortKey="inv_amt" sortState={varianceSort} onSort={toggleSort(setVarianceSort)} className="text-right">Total Invoiced</SortableTH>
                      <SortableTH sortKey="excess" sortState={varianceSort} onSort={toggleSort(setVarianceSort)} className="text-right">Excess Amount</SortableTH>
                      <SortableTH sortKey="pct" sortState={varianceSort} onSort={toggleSort(setVarianceSort)} className="text-right">Variance %</SortableTH>
                      <TH>Risk</TH>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const rows = metrics.exceptionsByType["Amount Mismatch"]
                        .filter(e => (e.invoice_amount || 0) > (e.po_amount || 0))
                        .map(exc => ({
                          ...exc,
                          excess: (exc.invoice_amount || 0) - (exc.po_amount || 0),
                          pct: exc.po_amount ? (((exc.invoice_amount || 0) - (exc.po_amount || 0)) / exc.po_amount * 100) : 0
                        }));
                      const sorted = sortData(rows, varianceSort, (item, key) => {
                        if (key === "po") return item.po_number || "";
                        if (key === "vendor") return item.vendor_id || "";
                        if (key === "po_amt") return item.po_amount || 0;
                        if (key === "inv_amt") return item.invoice_amount || 0;
                        if (key === "excess") return item.excess;
                        if (key === "pct") return item.pct;
                        return 0;
                      });
                      return sorted.slice(0, 20).map((exc, idx) => (
                        <TableRow key={idx} className="hover:bg-slate-50/40">
                          <TableCell className="font-mono text-sm">{exc.po_number}</TableCell>
                          <TableCell className="text-sm text-slate-500">{exc.vendor_id || '-'}</TableCell>
                          <TableCell className="text-right font-mono text-sm">${exc.po_amount?.toLocaleString(undefined, {minimumFractionDigits: 2}) || '0.00'}</TableCell>
                          <TableCell className="text-right font-mono text-sm">${exc.invoice_amount?.toLocaleString(undefined, {minimumFractionDigits: 2}) || '0.00'}</TableCell>
                          <TableCell className="text-right font-mono text-sm font-bold text-red-600">${exc.excess.toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                          <TableCell className="text-right"><span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${exc.pct > 50 ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'}`}>{exc.pct.toFixed(0)}%</span></TableCell>
                          <TableCell><SeverityBadge level={exc.pct > 50 ? "CRITICAL" : "HIGH"} /></TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 4: SEQUENCING VIOLATIONS */}
        <TabsContent value="seq" className="mt-0">
          {/* Row 1: KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <KPITile value={metrics.seq_inv_before_po_count ?? 0} label="Invoice Before PO Date" subtext="Invoice raised before PO was created" variant="red" />
            <KPITile value={metrics.seq_inv_before_grn_count ?? 0} label="Invoice Before GRN Date" subtext="Invoice raised before goods received" variant="red" />
            <KPITile value={metrics.seq_no_po_count ?? 0} label="Invoices with No PO" subtext={`${metrics.seq_no_po_pct ?? 0}% of all invoices - no PO reference`} variant="amber" />
          </div>

          {/* Row 2: Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
            <Card className="card-surface">
              <CardHeader className="pb-3 pt-5 px-6">
                <SectionTitle dotColor="bg-red-500">Sequencing Violation Summary</SectionTitle>
              </CardHeader>
              <CardContent className="px-6 pb-5">
                <div className="h-[220px]">
                  <Bar data={{
                    labels: ['Invoice Before PO', 'Invoice Before GRN', 'No PO Reference', 'Paid Before GRN'],
                    datasets: [{ data: [metrics.seq_inv_before_po_count ?? 0, metrics.seq_inv_before_grn_count ?? 0, metrics.seq_no_po_count ?? 0, metrics.seq_paid_before_grn_count ?? 0], backgroundColor: '#dc2626', borderRadius: 6 }]
                  }} options={horizontalBarOptions} />
                </div>
              </CardContent>
            </Card>

            <Card className="card-surface">
              <CardHeader className="pb-3 pt-5 px-6">
                <SectionTitle dotColor="bg-purple-500">Monthly No-PO Rate & No-GRN Rate (%)</SectionTitle>
              </CardHeader>
              <CardContent className="px-6 pb-5">
                <div className="h-[220px]">
                  {(metrics.seq_monthly_rates?.length > 0) && <Line data={{
                    labels: metrics.seq_monthly_rates.map(r => r.month),
                    datasets: [
                      { label: 'No-PO Rate', data: metrics.seq_monthly_rates.map(r => r.no_po_rate), borderColor: '#7c3aed', backgroundColor: 'rgba(124,58,237,0.08)', tension: 0.3, pointRadius: 3, pointBackgroundColor: '#7c3aed', fill: true },
                      { label: 'No-GRN Rate', data: metrics.seq_monthly_rates.map(r => r.no_grn_rate), borderColor: '#0d9488', backgroundColor: 'rgba(13,148,136,0.08)', tension: 0.3, pointRadius: 3, pointBackgroundColor: '#0d9488', fill: true }
                    ]
                  }} options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { labels: { font: { size: 11, family: fontFamily }, usePointStyle: true, pointStyleWidth: 8, color: '#64748b', padding: 12 } }, tooltip: { callbacks: { label: ctx => `${ctx.dataset.label}: ${ctx.raw}%` } } },
                    scales: {
                      x: { grid: { display: false }, border: { display: false }, ticks: { font: { size: 9, family: fontFamily }, color: '#94a3b8', maxRotation: 45 } },
                      y: { grid: { color: '#f1f5f9', drawBorder: false }, border: { display: false }, ticks: { font: { size: 11, family: fontFamily }, color: '#94a3b8', callback: v => v + '%' }, beginAtZero: true }
                    }
                  }} />}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Row 3: Invoice Before PO Table */}
          <Card className="card-surface mb-5">
            <CardHeader className="pb-3 pt-5 px-6">
              <SectionTitle dotColor="bg-red-500">Invoices Dated Before PO Creation Date</SectionTitle>
            </CardHeader>
            <CardContent className="px-6 pb-5">
              <ScrollArea className="h-[280px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableTH sortKey="inv" sortState={seqSort} onSort={toggleSort(setSeqSort)}>Invoice ID</SortableTH>
                      <SortableTH sortKey="po" sortState={seqSort} onSort={toggleSort(setSeqSort)}>PO ID</SortableTH>
                      <TH>Invoice Date</TH>
                      <TH>PO Date</TH>
                      <SortableTH sortKey="days" sortState={seqSort} onSort={toggleSort(setSeqSort)} className="text-right">Days Before PO</SortableTH>
                      <SortableTH sortKey="amount" sortState={seqSort} onSort={toggleSort(setSeqSort)} className="text-right">Gross Amount</SortableTH>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortData(metrics.seq_inv_before_po_details || [], seqSort, (item, key) => {
                      if (key === "inv") return item.invoice_number || "";
                      if (key === "po") return item.po_number || "";
                      if (key === "days") return item.days_before || 0;
                      if (key === "amount") return item.amount || 0;
                      return 0;
                    }).map((row, idx) => (
                      <TableRow key={idx} className="hover:bg-slate-50/40">
                        <TableCell className="font-mono text-sm">{row.invoice_number}</TableCell>
                        <TableCell className="font-mono text-sm">{row.po_number}</TableCell>
                        <TableCell className="text-sm text-slate-500">{row.invoice_date}</TableCell>
                        <TableCell className="text-sm text-slate-500">{row.po_date}</TableCell>
                        <TableCell className="text-right font-mono text-sm font-bold text-red-600">{row.days_before}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{'₹'}{row.amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                      </TableRow>
                    ))}
                    {(!metrics.seq_inv_before_po_details || metrics.seq_inv_before_po_details.length === 0) && (
                      <TableRow><TableCell colSpan={6} className="text-center text-emerald-600 py-6 text-sm">No sequencing violations found</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Row 4: Invoice Before GRN Table */}
          <Card className="card-surface mb-4">
            <CardHeader className="pb-3 pt-5 px-6">
              <SectionTitle dotColor="bg-amber-500">Invoices Dated Before First GRN Date</SectionTitle>
            </CardHeader>
            <CardContent className="px-6 pb-5">
              <ScrollArea className="h-[280px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableTH sortKey="inv" sortState={seqSort} onSort={toggleSort(setSeqSort)}>Invoice ID</SortableTH>
                      <SortableTH sortKey="po" sortState={seqSort} onSort={toggleSort(setSeqSort)}>PO ID</SortableTH>
                      <TH>Invoice Date</TH>
                      <TH>First GRN Date</TH>
                      <SortableTH sortKey="days" sortState={seqSort} onSort={toggleSort(setSeqSort)} className="text-right">Days Before GRN</SortableTH>
                      <SortableTH sortKey="amount" sortState={seqSort} onSort={toggleSort(setSeqSort)} className="text-right">Gross Amount</SortableTH>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortData(metrics.seq_inv_before_grn_details || [], seqSort, (item, key) => {
                      if (key === "inv") return item.invoice_number || "";
                      if (key === "po") return item.po_number || "";
                      if (key === "days") return item.days_before || 0;
                      if (key === "amount") return item.amount || 0;
                      return 0;
                    }).map((row, idx) => (
                      <TableRow key={idx} className="hover:bg-slate-50/40">
                        <TableCell className="font-mono text-sm">{row.invoice_number}</TableCell>
                        <TableCell className="font-mono text-sm">{row.po_number}</TableCell>
                        <TableCell className="text-sm text-slate-500">{row.invoice_date}</TableCell>
                        <TableCell className="text-sm text-slate-500">{row.first_grn_date}</TableCell>
                        <TableCell className="text-right font-mono text-sm font-bold text-red-600">{row.days_before}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{'₹'}{row.amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                      </TableRow>
                    ))}
                    {(!metrics.seq_inv_before_grn_details || metrics.seq_inv_before_grn_details.length === 0) && (
                      <TableRow><TableCell colSpan={6} className="text-center text-emerald-600 py-6 text-sm">No sequencing violations found</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>


        {/* TAB 5: PAYMENT RISK */}
        <TabsContent value="pay" className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <KPITile value={`$${metrics.overInvoicedAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`} label="Over-Invoiced Amount" subtext={`${metrics.overInvoiced} over-invoiced POs`} variant="red" />
            <KPITile value={`$${metrics.missingPOAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`} label="Missing PO Amount" subtext={`${metrics.missingPOs} invoices without PO`} variant="red" />
            <KPITile value={`$${metrics.totalVariance.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`} label="Total Variance" subtext="Absolute amount difference" variant="amber" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
            <Card className="card-surface">
              <CardHeader className="pb-3 pt-5 px-6">
                <SectionTitle dotColor="bg-red-500">Materiality Risk by Category - Amount ($K)</SectionTitle>
              </CardHeader>
              <CardContent className="px-6 pb-5">
                <div className="h-[230px]">
                  {paymentRiskAmountData && <Bar data={paymentRiskAmountData} options={{...barOptions, scales: {...barOptions.scales, y: {...barOptions.scales.y, ticks: {...barOptions.scales.y.ticks, callback: v => '$' + v.toLocaleString()}}}}} />}
                </div>
              </CardContent>
            </Card>

            <Card className="card-surface">
              <CardHeader className="pb-3 pt-5 px-6">
                <SectionTitle dotColor="bg-amber-500">Materiality Risk by Category - Count</SectionTitle>
              </CardHeader>
              <CardContent className="px-6 pb-5">
                <div className="h-[230px]">
                  {paymentRiskCountData && <Bar data={paymentRiskCountData} options={barOptions} />}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Payment Risk Summary Table */}
          <Card className="card-surface">
            <CardHeader className="pb-3 pt-5 px-6">
              <SectionTitle dotColor="bg-slate-400">Risk Summary - Exception Analysis</SectionTitle>
            </CardHeader>
            <CardContent className="px-6 pb-5">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TH>Risk Type</TH>
                    <TH>Description</TH>
                    <TH className="text-right"># Items</TH>
                    <TH className="text-right">Amount at Risk</TH>
                    <TH>Severity</TH>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {metrics.overInvoiced > 0 && (
                    <TableRow className="hover:bg-slate-50/40">
                      <TableCell className="font-semibold text-sm">Over-Invoiced</TableCell>
                      <TableCell className="text-sm text-slate-500">Invoice amount exceeds PO amount</TableCell>
                      <TableCell className="text-right font-mono text-sm">{metrics.overInvoiced}</TableCell>
                      <TableCell className="text-right font-mono text-sm font-bold text-red-600">${metrics.overInvoicedAmount.toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                      <TableCell><SeverityBadge level="CRITICAL" /></TableCell>
                    </TableRow>
                  )}
                  {metrics.missingPOs > 0 && (
                    <TableRow className="hover:bg-slate-50/40">
                      <TableCell className="font-semibold text-sm">Missing PO Reference</TableCell>
                      <TableCell className="text-sm text-slate-500">Invoice/GRN references non-existent PO</TableCell>
                      <TableCell className="text-right font-mono text-sm">{metrics.missingPOs}</TableCell>
                      <TableCell className="text-right font-mono text-sm font-bold text-red-600">${metrics.missingPOAmount.toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                      <TableCell><SeverityBadge level="CRITICAL" /></TableCell>
                    </TableRow>
                  )}
                  {metrics.underInvoiced > 0 && (
                    <TableRow className="hover:bg-slate-50/40">
                      <TableCell className="font-semibold text-sm">Under-Invoiced</TableCell>
                      <TableCell className="text-sm text-slate-500">Invoice amount less than PO amount</TableCell>
                      <TableCell className="text-right font-mono text-sm">{metrics.underInvoiced}</TableCell>
                      <TableCell className="text-right font-mono text-sm font-bold text-blue-600">${(metrics.totalVariance - metrics.overInvoicedAmount).toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                      <TableCell><SeverityBadge level="MEDIUM" /></TableCell>
                    </TableRow>
                  )}
                  {metrics.quantityMismatches > 0 && (
                    <TableRow className="hover:bg-slate-50/40">
                      <TableCell className="font-semibold text-sm">Quantity Mismatch</TableCell>
                      <TableCell className="text-sm text-slate-500">GRN qty outside ±2% tolerance</TableCell>
                      <TableCell className="text-right font-mono text-sm">{metrics.quantityMismatches}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-slate-400">-</TableCell>
                      <TableCell><SeverityBadge level="HIGH" /></TableCell>
                    </TableRow>
                  )}
                  {metrics.total === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-emerald-600 font-medium py-6 text-sm">
                        No risk items identified
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Footer Actions */}
      <div className="flex items-center justify-between pt-8 mt-2">
        <Button variant="ghost" onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 rounded-xl" data-testid="dashboard-back-button">
          <ListChecks size={16} />
          Back to Review
        </Button>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleExportPdf} disabled={exportingPdf || loading} className="flex items-center gap-2 rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50" data-testid="export-pdf-button">
            <FileText size={16} />
            {exportingPdf ? 'Generating...' : 'Download PDF'}
          </Button>
          <Button variant="outline" onClick={handleExport} disabled={exporting || loading} className="flex items-center gap-2 rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50" data-testid="export-excel-button">
            <Download size={16} />
            {exporting ? 'Exporting...' : 'Export Excel'}
          </Button>
          <Button onClick={onNewRun} className="btn-primary flex items-center gap-2" data-testid="new-run-button">
            <RotateCcw size={16} />
            New Run
          </Button>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#062B55] text-slate-400/70 text-[11px] text-center py-3.5 mt-8 rounded-2xl tracking-wide">
        Three-Way Match Dashboard · AP Governance Analysis · {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} · CONFIDENTIAL
      </div>
    </div>
  );
}
